import { FieldError } from 'react-hook-form'

import { FormControl, FormField, FormItem, FormLabel, FormMessage, useFormField } from 'shadcdn/ui/form'
import { Input, TextAreaInput } from 'shadcdn/ui/input'
import { Checkbox } from 'shadcdn/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcdn/ui/select'
import { cn } from 'utils/cn'

import './Form.css'

type CustomFormField = {
  form: any
  name: string
  label: React.ReactNode
  placeholder?: string
  helpText?: string | React.ReactNode
  className?: string
  inputClassName?: string
  growWrapClassName?: string
  disabled?: boolean
}

export const TextInputFormField = (props: CustomFormField) => {
  const { form, name, label, placeholder, className, inputClassName, disabled } = props
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              placeholder={placeholder}
              {...field}
              value={field.value ?? ''}
              disabled={disabled}
              className={inputClassName}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export const CheckboxFormField = (props: CustomFormField) => {
  const { form, name, label, className } = props
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex">
            <FormControl>
              <Checkbox {...field} checked={field.value} onCheckedChange={(checked) => field.onChange(checked)} />
            </FormControl>
            <FormLabel className="ml-1">{label}</FormLabel>
          </div>
        </FormItem>
      )}
    />
  )
}

export const TextAreaFormField = (props: CustomFormField) => {
  const { form, name, label, placeholder, className, growWrapClassName, helpText } = props
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {/* */}
            <div className={cn('grow-wrap', growWrapClassName)} data-replicated-value={field.value}>
              <TextAreaInput placeholder={placeholder} {...field} />
            </div>
          </FormControl>
          {helpText && <div className="text-xs text-gray-500">{helpText}</div>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export const ArrayTextAreaFormField = (props: CustomFormField) => {
  const { form, name, label, placeholder, className, growWrapClassName, disabled } = props
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const onChange = field.onChange
        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          const value = e.target.value.split('\n')
          const newEvent = { target: { value } }
          onChange(newEvent)
        }
        const value = (field.value ?? []).join('\n')

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { error = [] } = useFormField()

        const errors: FieldError[] = error as unknown as FieldError[]

        return (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className={cn('grow-wrap', growWrapClassName)} data-replicated-value={value}>
                <TextAreaInput
                  placeholder={placeholder}
                  {...field}
                  onChange={handleChange}
                  disabled={disabled}
                  value={value}
                  spellCheck={false}
                />
              </div>
            </FormControl>
            {errors?.map((error, index) =>
              error ? (
                <p key={index} className={cn('text-xs font-medium text-destructive')}>
                  Line {index + 1} "{value.split('\n')[index]}": {String(error.message)}
                </p>
              ) : null
            )}
          </FormItem>
        )
      }}
    />
  )
}

type SelectOption = {
  value: string
  label: string
}

export const SelectFormField = (props: CustomFormField & { 
  options: SelectOption[]
  colorMap?: Record<string, string>
}) => {
  const { form, name, label, placeholder, className, disabled, options, colorMap } = props
  
  const getOptionClassName = (value: string) => {
    return colorMap?.[value] ?? ''
  }

  const getTriggerClassName = (selectedValue: string) => {
    const colorClass = colorMap?.[selectedValue]
    return colorClass ? colorClass : undefined
  }

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
              <SelectTrigger className={cn('py-3 text-base', getTriggerClassName(field.value))}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className={getOptionClassName(option.value)}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
